import { useEffect, useState } from 'react';
import { apiJson } from '../api/client.js';
import { SearchableSelect } from '../components/SearchableSelect.jsx';
import {
  AssetFileDropzone,
  SelectedUploadFiles,
  mergeFiles,
  splitBySize,
  usePageFileDrop,
} from '../components/AssetFileDropzone.jsx';
import { AssetLibraryList } from '../components/AssetLibraryList.jsx';
import { AssetLibraryAiSearch } from '../components/AssetLibraryAiSearch.jsx';
import { FormInput, SearchForm } from '../components/FormControls.jsx';
import { Modal } from '../components/Modal.jsx';
import { useAuth } from '../context/AuthContext.jsx';

const STATUS_LABELS = {
  pending: 'Pending review',
  approved: 'Approved',
  rejected: 'Rejected',
  fulfilled: 'Fulfilled',
};

export function MyAssetsPage() {
  const { user } = useAuth();
  const caps = user?.capabilities ?? {};
  const [tab, setTab] = useState('library');
  const [requests, setRequests] = useState([]);
  const [library, setLibrary] = useState([]);
  const [collections, setCollections] = useState([]);
  const [myUploads, setMyUploads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState('');
  const [collectionFilter, setCollectionFilter] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', reference_url: '', project_id: '' });
  const [uploadForm, setUploadForm] = useState({ project_id: '', collection_slug: '', label: '' });
  const [uploadFiles, setUploadFiles] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [showRequest, setShowRequest] = useState(false);

  const closeUpload = () => {
    setShowUpload(false);
    setUploadFiles([]);
    setUploadForm({ project_id: '', collection_slug: '', label: '' });
  };

  const acceptDroppedFiles = (incoming) => {
    if (busy) return;
    const { accepted, tooLarge } = splitBySize(incoming);
    if (tooLarge.length) {
      setError(
        `${tooLarge.map((file) => file.name).join(', ')} ${tooLarge.length === 1 ? 'is' : 'are'} larger than 20 MB.`,
      );
    } else {
      setError('');
    }
    if (!accepted.length) return;
    setUploadFiles((current) => mergeFiles(current, accepted));
    setShowUpload(true);
  };

  const { active: fileDragActive, rootRef: fileDropRef } = usePageFileDrop(acceptDroppedFiles);

  const closeRequest = () => {
    setShowRequest(false);
    setForm({ title: '', description: '', reference_url: '', project_id: '' });
  };

  const load = () => {
    const params = new URLSearchParams();
    if (search.trim()) params.set('q', search.trim());
    if (collectionFilter) params.set('collection_slug', collectionFilter);
    const q = params.toString() ? `?${params}` : '';
    return Promise.all([
      apiJson('/api/my/asset-requests'),
      apiJson(`/api/my/assets/library${q}`),
      apiJson('/api/my/assets/collections').catch(() => []),
      apiJson('/api/my/assets/my-uploads').catch(() => []),
      apiJson('/api/my/projects').catch(() => []),
    ])
      .then(([reqs, lib, cols, uploads, projs]) => {
        setRequests(reqs);
        setLibrary(lib);
        setCollections(cols);
        setMyUploads(uploads);
        setProjects(Array.isArray(projs) ? projs : projs?.data ?? []);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    void load();
  }, []);

  const searchLibrary = (e) => {
    e.preventDefault();
    setLoading(true);
    void load();
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await apiJson('/api/my/asset-requests', {
        method: 'POST',
        body: {
          title: form.title.trim(),
          description: form.description.trim() || null,
          reference_url: form.reference_url.trim() || null,
          project_id: form.project_id ? Number(form.project_id) : null,
        },
      });
      closeRequest();
      setTab('requests');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const submitUpload = async (e) => {
    e.preventDefault();
    if (!uploadFiles.length) return;
    setBusy(true);
    setError('');
    const failures = [];
    const failedFiles = [];
    try {
      for (const file of uploadFiles) {
        const body = new FormData();
        body.append('file', file);
        if (uploadForm.project_id) body.append('project_id', uploadForm.project_id);
        if (uploadForm.collection_slug) body.append('collection_slug', uploadForm.collection_slug);
        if (uploadFiles.length === 1 && uploadForm.label.trim()) body.append('label', uploadForm.label.trim());
        try {
          await apiJson('/api/my/assets/upload', { method: 'POST', body });
        } catch (err) {
          failures.push(`${file.name}: ${err.message}`);
          failedFiles.push(file);
        }
      }
      if (failedFiles.length) {
        setUploadFiles(failedFiles);
        setError(failures.join(' '));
      } else {
        closeUpload();
        setTab('uploads');
      }
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={fileDropRef}>
      {fileDragActive && !showUpload ? (
        <div className="asset-drop-overlay" role="status">
          <div className="asset-drop-overlay-card">
            <strong>Drop files to upload</strong>
            <span className="muted small">Your agency reviews files before they appear in the library.</span>
          </div>
        </div>
      ) : null}
      <div
        className="row-actions"
        style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.5rem' }}
      >
        <h2 style={{ marginTop: 0, marginBottom: 0 }} data-tour="page-title">
          Media & assets
        </h2>
        <div className="row-actions">
          <button type="button" className="btn-secondary" onClick={() => setShowUpload(true)}>
            Upload file
          </button>
          <button type="button" className="btn-primary" onClick={() => setShowRequest(true)}>
            New request
          </button>
        </div>
      </div>
      <p className="muted small">Browse shared files or request new assets from your agency.</p>

      <div className="row-actions" style={{ marginBottom: '1rem' }} data-tour="assets-tabs">
        {['library', 'uploads', 'requests'].map((t) => (
          <button
            key={t}
            type="button"
            className={tab === t ? 'btn-secondary' : 'btn-link'}
            onClick={() => setTab(t)}
          >
            {t === 'library'
              ? `Library (${library.length})`
              : t === 'uploads'
                ? `My uploads (${myUploads.length})`
                : `Requests (${requests.length})`}
          </button>
        ))}
      </div>

      {error ? <p className="err">{error}</p> : null}
      {loading ? <p className="muted">Loading…</p> : null}

      {tab === 'library' && !loading ? (
        <>
          <div style={{ marginBottom: '1rem' }}>
            <AssetFileDropzone
              active={fileDragActive && !showUpload}
              busy={busy}
              onBrowse={acceptDroppedFiles}
              hint="or click to browse"
            />
          </div>
          {caps.ai_asset_search ? (
            <AssetLibraryAiSearch endpoint="/api/my/assets/library/ai-search" showMeta />
          ) : null}
          {collections.length ? (
            <label className="field" style={{ marginBottom: '1rem' }}>
              <span className="muted small">Collection</span>
              <SearchableSelect value={collectionFilter} onChange={(e) => setCollectionFilter(e.target.value)}>
                <option value="">All collections</option>
                {collections.map((c) => (
                  <option key={c.id} value={c.collection_slug || ''}>
                    {c.title}
                  </option>
                ))}
              </SearchableSelect>
            </label>
          ) : null}
          <SearchForm onSubmit={searchLibrary}>
            <FormInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search assets…"
            />
            <button type="submit" className="btn-secondary">
              Search
            </button>
          </SearchForm>
          <AssetLibraryList items={library} showMeta />
        </>
      ) : null}

      {tab === 'uploads' && !loading ? (
        <>
          {!myUploads.length ? <p className="muted">No pending uploads.</p> : null}
          <ul className="list-plain">
            {myUploads.map((u) => (
              <li key={u.id} className="auth-card flat" style={{ marginBottom: '0.75rem' }}>
                <strong>{u.label || u.file_name}</strong>
                <span className={`badge badge-${u.approval_status === 'pending' ? 'warn' : 'err'}`}>
                  {' '}
                  {u.approval_status}
                </span>
                {u.collection_title ? <div className="muted small">Collection: {u.collection_title}</div> : null}
                {u.approval_note ? <div className="muted small">Note: {u.approval_note}</div> : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <Modal
        open={showUpload}
        title="Upload center"
        description="Files are reviewed by your agency before appearing in the library."
        onClose={closeUpload}
      >
        <form className="stack" onSubmit={(e) => void submitUpload(e)}>
          {error ? <p className="err small">{error}</p> : null}
          <AssetFileDropzone
            active={fileDragActive}
            busy={busy}
            onBrowse={acceptDroppedFiles}
            hint="or click to browse"
          />
          <SelectedUploadFiles
            files={uploadFiles}
            disabled={busy}
            onRemove={(file) =>
              setUploadFiles((current) =>
                current.filter(
                  (item) =>
                    !(item.name === file.name && item.size === file.size && item.lastModified === file.lastModified),
                ),
              )
            }
          />
          {uploadFiles.length <= 1 ? (
            <label className="field">
              <span>Label</span>
              <input
                value={uploadForm.label}
                onChange={(e) => setUploadForm((f) => ({ ...f, label: e.target.value }))}
              />
            </label>
          ) : null}
          {projects.length ? (
            <label className="field">
              <span>Project (optional)</span>
              <SearchableSelect
                value={uploadForm.project_id}
                onChange={(e) => setUploadForm((f) => ({ ...f, project_id: e.target.value }))}
              >
                <option value="">— None —</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </SearchableSelect>
            </label>
          ) : null}
          {collections.length ? (
            <label className="field">
              <span>Collection (optional)</span>
              <SearchableSelect
                value={uploadForm.collection_slug}
                onChange={(e) => setUploadForm((f) => ({ ...f, collection_slug: e.target.value }))}
              >
                <option value="">Auto-route</option>
                {collections.map((c) => (
                  <option key={c.id} value={c.collection_slug || ''}>
                    {c.title}
                  </option>
                ))}
              </SearchableSelect>
            </label>
          ) : null}
          <div className="row-actions" style={{ marginTop: '0.5rem' }}>
            <button type="button" className="btn-secondary" onClick={closeUpload} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={busy || !uploadFiles.length}>
              {busy ? 'Uploading…' : uploadFiles.length > 1 ? `Upload ${uploadFiles.length} files` : 'Upload file'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal open={showRequest} title="New asset request" onClose={closeRequest}>
        <form className="stack" onSubmit={(e) => void submit(e)}>
          <label className="field">
            <span>Title *</span>
            <input
              required
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />
          </label>
          <label className="field">
            <span>Description</span>
            <textarea
              rows={3}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </label>
          <label className="field">
            <span>Reference link</span>
            <input
              type="url"
              value={form.reference_url}
              onChange={(e) => setForm((f) => ({ ...f, reference_url: e.target.value }))}
            />
          </label>
          {projects.length ? (
            <label className="field">
              <span>Project (optional)</span>
              <SearchableSelect
                value={form.project_id}
                onChange={(e) => setForm((f) => ({ ...f, project_id: e.target.value }))}
              >
                <option value="">— None —</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </SearchableSelect>
            </label>
          ) : null}
          <div className="row-actions" style={{ marginTop: '0.5rem' }}>
            <button type="button" className="btn-secondary" onClick={closeRequest} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? 'Submitting…' : 'Submit request'}
            </button>
          </div>
        </form>
      </Modal>

      {tab === 'requests' && !loading ? (
        <>
          {!requests.length ? <p className="muted">No requests yet.</p> : null}
          <ul className="list-plain">
            {requests.map((r) => (
              <li key={r.id} className="auth-card flat" style={{ marginBottom: '0.75rem' }}>
                <strong>{r.title}</strong>
                <span className={`badge badge-${r.status === 'pending' ? 'warn' : r.status === 'rejected' ? 'err' : 'ok'}`}>
                  {' '}
                  {STATUS_LABELS[r.status] ?? r.status}
                </span>
                {r.project?.name ? <div className="muted small">Project: {r.project.name}</div> : null}
                {r.description ? <p className="muted small">{r.description}</p> : null}
                {r.admin_notes ? <div className="muted small">Team note: {r.admin_notes}</div> : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}
